import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { ImageModule } from 'primeng/image';
import { GalleriaModule } from 'primeng/galleria';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MenuModule } from 'primeng/menu';
import { OverlayModule } from 'primeng/overlay';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SliderModule } from 'primeng/slider';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SplitButtonModule } from 'primeng/splitbutton';
import { PanelModule } from 'primeng/panel';
import { DataViewModule } from 'primeng/dataview';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ConfirmationService } from 'primeng/api';
import { MenuItem } from 'primeng/api';
import { MediaService } from '../../services/media.service';
import { DownloadService } from '../../services/download.service';
import { NotificationService } from '../../services/notification.service';
import { PhotoCacheService } from '../../services/photo-cache.service';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Overlay } from 'primeng/overlay';

export interface MediaItem {
  id: string;
  sessionId: string;
  name: string;
  key: string;
  type: string;
  size: number;
  url: string;
  label?: string;
  sortOrder: number;
  thumbnailUrl?: string;
  cachedThumbnailUrl?: string; // Cached blob URL for thumbnail
  cachedUrl?: string; // Cached blob URL for full image
  createdAt: Date;
  status?: 'uploading' | 'ready' | 'error';
  progress?: number;
  selected?: boolean;
}

export interface GridSettings {
  size: 'small' | 'medium' | 'large' | 'xlarge';
  columns: number;
  showLabels: boolean;
  showMetadata: boolean;
}

@Component({
    selector: 'app-media-gallery',
    templateUrl: './media-gallery.component.html',
    styleUrls: ['./media-gallery.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        CardModule,
        DialogModule,
        ImageModule,
        GalleriaModule,
        TagModule,
        ProgressSpinnerModule,
        TooltipModule,
        ConfirmDialogModule,
        MenuModule,
        OverlayModule,
        CheckboxModule,
        InputTextModule,
        SliderModule,
        SelectModule,
        SelectButtonModule,
        SplitButtonModule,
        PanelModule,
        DataViewModule,
        DragDropModule
    ]
})
export class MediaGalleryComponent implements OnInit, OnDestroy, OnChanges {
  @Input() sessionId: string = '';
  @Input() media: MediaItem[] = [];
  @Input() maxMedia: number = 100;
  @Input() showControls: boolean = true;
  @Input() isCoordinator: boolean = false;
  @Input() allowReorder: boolean = true;
  @Input() allowBatchOperations: boolean = true;
  @Input() gridSize?: 'small' | 'medium' | 'large' = 'medium';
  @Input() selectedItemIds?: Set<string>;
  @Output() mediaAdded = new EventEmitter<MediaItem>();
  @Output() mediaDeleted = new EventEmitter<string>();
  @Output() mediaUpdated = new EventEmitter<MediaItem>();
  @Output() mediaCaptureRequested = new EventEmitter<void>();
  @Output() selectionChanged = new EventEmitter<Set<string>>();

  @ViewChild('labelDialog') labelDialog!: Overlay;

  displayGalleria: boolean = false;
  selectedMediaIndex: number = 0;
  loading: boolean = false;
  
  // Selection and batch operations
  selectedItems: Set<string> = new Set();
  selectAll: boolean = false;
  batchMenuItems: MenuItem[] = [];
  
  // Grid settings (default to medium: 4 columns)
  gridSettings: GridSettings = {
    size: 'medium',
    columns: 4,
    showLabels: true,
    showMetadata: false
  };
  
  // Grid size options
  gridSizeOptions: Array<{
    label: string;
    value: GridSettings['size'];
    columns: number;
    icon: string;
  }> = [
    { label: 'Small', value: 'small', columns: 6, icon: 'pi pi-th-large' },
    { label: 'Medium', value: 'medium', columns: 4, icon: 'pi pi-th-large' },
    { label: 'Large', value: 'large', columns: 2, icon: 'pi pi-th-large' },
    { label: 'Extra Large', value: 'xlarge', columns: 1, icon: 'pi pi-th-large' }
  ];
  
  // Media type filter
  selectedMediaType: 'photos' | 'videos' = 'photos';
  mediaTypeOptions = [
    { label: 'Photos', icon: 'pi pi-image', value: 'photos' },
    { label: 'Videos', icon: 'pi pi-video', value: 'videos' }
  ];
  
  // Labeling
  showLabelDialog: boolean = false;
  labelForm: FormGroup;
  labelingItems: MediaItem[] = [];
  
  // Filtering and sorting
  filterValue: string = '';
  sortBy: 'name' | 'date' | 'size' | 'label' = 'date';
  sortOrder: 'asc' | 'desc' = 'desc';
  
  private subscriptions: Subscription[] = [];
  private apiUrl = `${environment.apiUrl}/media`;
  
  // Performance toggle - photo caching with conservative settings for optimal performance
  // Set to true to enable background caching, false for maximum capture performance
  private readonly enablePhotoCaching = false; // DISABLED for best capture performance
  
  // Conservative caching settings (when enabled)
  private readonly maxCacheItems = 3; // Only cache first 3 visible items
  private readonly cacheDelayMs = 5000; // Wait 5 seconds after photo capture before caching
  
  // Periodic refresh for video processing
  private refreshInterval: any;
  private refreshCount = 0;
  private readonly maxRefreshAttempts = 10; // Stop refreshing after 10 attempts (5 minutes)
  private readonly refreshIntervalMs = 30000; // Refresh every 30 seconds
  
  // Composition tracking
  public compositionStatus: any = null;
  public hasActiveCompositions = false;

  constructor(
    private mediaService: MediaService,
    private downloadService: DownloadService,
    private notificationService: NotificationService,
    private confirmationService: ConfirmationService,
    private fb: FormBuilder,
    private http: HttpClient,
    private photoCacheService: PhotoCacheService
  ) {
    this.labelForm = this.fb.group({
      label: ['']
    });
  }

  ngOnInit() {
    this.setupBatchMenuItems();
    
    // Use smaller thumbnails by default for coordinator
    if (this.isCoordinator) {
      this.gridSettings.size = 'small';
      this.gridSettings.showLabels = false; // Hide labels to save space in drawer
    }
    
    // Only load media from API if no media was passed as input
    if (!this.media || this.media.length === 0) {
      this.loadMedia();
      
      // Set up periodic refresh to catch videos processed after session ends
      this.startPeriodicRefresh();
    } else {
      // Media was provided as input, sort it
      this.sortMedia();
      // Preload visible thumbnails
      this.preloadVisibleThumbnails();
    }
    this.updateGridColumns();
    
    // Subscribe to MediaService for new photos added during the session
    // Only if we're not using input media (i.e., loading from service)
    if (!this.media || this.media.length === 0) {
      const mediaSubscription = this.mediaService.media$.subscribe(mediaItems => {
        if (mediaItems && mediaItems.length > 0) {
          // Update our local media array
          this.media = [...mediaItems]; // Create a copy
          this.sortMedia();
          
          // PERFORMANCE FIX: Only cache thumbnails in background after a longer delay
          // Don't immediately cache every new photo - this was blocking photo capture
          setTimeout(() => {
            this.preloadVisibleThumbnails();
          }, this.cacheDelayMs); // Wait 5 seconds before starting background caching
        }
      });
      this.subscriptions.push(mediaSubscription);
    }
    
    // Add keyboard event listener for photo navigation
    document.addEventListener('keydown', this.onKeydown.bind(this));
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle media input changes
    if (changes['media'] && !changes['media'].firstChange) {
      console.log('Media input changed:', this.media);
      if (this.media && this.media.length > 0) {
        // Only sort if this component is managing its own controls
        // If showControls is false, the parent is managing sorting
        if (this.showControls) {
          this.sortMedia();
        }
        // Preload visible thumbnails when media changes
        setTimeout(() => this.preloadVisibleThumbnails(), 100);
      }
    }
    
    // Handle gridSize input changes
    if (changes['gridSize'] && this.gridSize) {
      this.updateGridSize(this.gridSize);
    }
    
    // Handle selectedItemIds input changes from parent
    if (changes['selectedItemIds'] && this.selectedItemIds) {
      this.selectedItems = new Set(this.selectedItemIds);
      this.selectAll = this.selectedItems.size === this.filteredMedia.length && this.filteredMedia.length > 0;
      this.updateBatchMenuLabels();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Remove keyboard event listener
    document.removeEventListener('keydown', this.onKeydown.bind(this));
    
    // Clear periodic refresh interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    
    // Clear cached blob URLs to prevent memory leaks
    this.media.forEach(item => {
      if (item.cachedThumbnailUrl && item.cachedThumbnailUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.cachedThumbnailUrl);
      }
      if (item.cachedUrl && item.cachedUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.cachedUrl);
      }
    });
  }

  setupBatchMenuItems() {
    this.batchMenuItems = [
      {
        label: `Label Selected (${this.selectedItems.size})`,
        icon: 'pi pi-tag',
        command: () => this.openBatchLabelDialog(),
        disabled: this.selectedItems.size === 0
      },
      {
        label: `Download Selected (${this.selectedItems.size})`,
        icon: 'pi pi-download',
        command: () => this.downloadSelected(),
        disabled: this.selectedItems.size === 0
      },
      {
        separator: true
      },
      {
        label: `Delete Selected (${this.selectedItems.size})`,
        icon: 'pi pi-trash',
        command: () => this.deleteSelected(),
        disabled: this.selectedItems.size === 0,
        styleClass: 'text-red-500'
      }
    ];
  }

  async loadMedia() {
    if (!this.sessionId) return;
    
    try {
      this.loading = true;
      const response = await this.http.get<any>(`${this.apiUrl}/session/${this.sessionId}`).toPromise();
      if (response?.mediaFiles) {
        this.media = response.mediaFiles.map((item: any) => ({
          ...item,
          selected: false
        }));
        this.sortMedia();
        // Preload visible thumbnails after loading
        this.preloadVisibleThumbnails();
      }
    } catch (error) {
      this.notificationService.showToast('error', 'Error', 'Failed to load media files');
    } finally {
      this.loading = false;
    }
  }

  // Grid controls
  updateGridSize(size: string | GridSettings['size']) {
    const validSizes: GridSettings['size'][] = ['small', 'medium', 'large', 'xlarge'];
    if (validSizes.includes(size as GridSettings['size'])) {
      this.gridSettings.size = size as GridSettings['size'];
      this.updateGridColumns();
      // Preload visible thumbnails as the number of visible items may have changed
      setTimeout(() => this.preloadVisibleThumbnails(), 100);
    }
  }

  updateGridColumns() {
    const sizeConfig = this.gridSizeOptions.find(opt => opt.value === this.gridSettings.size);
    if (sizeConfig) {
      this.gridSettings.columns = sizeConfig.columns;
    }
  }

  getGridItemClass(): string {
    const baseClass = 'gallery-item';
    const sizeClass = `gallery-item-${this.gridSettings.size}`;
    return `${baseClass} ${sizeClass}`;
  }

  // Selection methods
  toggleSelectAll() {
    this.selectAll = !this.selectAll;
    if (this.selectAll) {
      this.selectedItems = new Set(this.filteredMedia.map(item => item.id));
    } else {
      this.selectedItems.clear();
    }
    this.updateBatchMenuLabels();
    this.selectionChanged.emit(new Set(this.selectedItems));
  }

  toggleItemSelection(item: MediaItem) {
    if (this.selectedItems.has(item.id)) {
      this.selectedItems.delete(item.id);
    } else {
      this.selectedItems.add(item.id);
    }
    
    this.selectAll = this.selectedItems.size === this.filteredMedia.length;
    this.updateBatchMenuLabels();
    this.selectionChanged.emit(new Set(this.selectedItems));
  }

  isItemSelected(item: MediaItem): boolean {
    return this.selectedItems.has(item.id);
  }

  updateBatchMenuLabels() {
    this.setupBatchMenuItems();
  }

  // Drag and drop
  onDrop(event: CdkDragDrop<MediaItem[]>) {
    if (!this.allowReorder) return;
    
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.media, event.previousIndex, event.currentIndex);
      this.updateSortOrder();
    }
  }

  async updateSortOrder() {
    try {
      const mediaIds = this.media.map(item => item.id);
      await this.http.put(`${this.apiUrl}/session/${this.sessionId}/reorder`, {
        mediaIds
      }).toPromise();
    } catch (error) {
      this.notificationService.showToast('error', 'Error', 'Failed to update sort order');
      // Reload to reset order
      this.loadMedia();
    }
  }

  // Labeling
  openLabelDialog(item: MediaItem) {
    this.labelingItems = [item];
    this.labelForm.patchValue({ label: item.label || '' });
    this.showLabelDialog = true;
  }

  openBatchLabelDialog() {
    const selectedMedia = this.media.filter(item => this.selectedItems.has(item.id));
    if (selectedMedia.length === 0) return;
    
    this.labelingItems = selectedMedia;
    // If all selected items have the same label, show it
    const commonLabel = selectedMedia.every(item => item.label === selectedMedia[0].label) 
      ? selectedMedia[0].label || '' 
      : '';
    this.labelForm.patchValue({ label: commonLabel });
    this.showLabelDialog = true;
  }

  async saveLabel() {
    const labelValue = this.labelForm.value.label?.trim();
    
    try {
      if (this.labelingItems.length === 1) {
        // Single item update
        const item = this.labelingItems[0];
        await this.http.patch(`${this.apiUrl}/${item.id}`, {
          label: labelValue
        }).toPromise();
        
        item.label = labelValue;
        this.mediaUpdated.emit(item);
      } else {
        // Batch update
        const mediaIds = this.labelingItems.map(item => item.id);
        await this.http.put(`${this.apiUrl}/batch/update`, {
          mediaIds,
          label: labelValue
        }).toPromise();
        
        // Update local items
        this.labelingItems.forEach(item => {
          item.label = labelValue;
          this.mediaUpdated.emit(item);
        });
      }
      
      this.showLabelDialog = false;
      this.notificationService.showToast('success', 'Success', 'Label updated successfully');
    } catch (error) {
      this.notificationService.showToast('error', 'Error', 'Failed to update label');
    }
  }

  // Batch operations
  async downloadSelected() {
    const selectedMedia = this.media.filter(item => this.selectedItems.has(item.id));
    if (selectedMedia.length === 0) return;

    try {
      // Open each file in a new tab with a small delay to prevent popup blocking
      for (let i = 0; i < selectedMedia.length; i++) {
        const item = selectedMedia[i];
        const downloadUrl = await this.downloadService.getOjectDetails(item.key);
        if (downloadUrl?.url) {
          window.open(downloadUrl.url, '_blank', 'noopener,noreferrer');
          // Add small delay between opening tabs to prevent browser blocking
          if (i < selectedMedia.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
      this.notificationService.showToast('success', 'Success', `Opening ${selectedMedia.length} files in new tabs`);
    } catch (error) {
      this.notificationService.showToast('error', 'Error', 'Failed to open files');
    }
  }

  async downloadSingleItem(item: MediaItem) {
    try {
      const downloadUrl = await this.downloadService.getOjectDetails(item.key);
      if (downloadUrl?.url) {
        // Open in new tab/window instead of downloading
        window.open(downloadUrl.url, '_blank', 'noopener,noreferrer');
        
        this.notificationService.showToast('success', 'Success', 'Opening file in new tab');
      }
    } catch (error) {
      this.notificationService.showToast('error', 'Error', 'Failed to open file');
    }
  }

  deleteSelected() {
    const selectedMedia = this.media.filter(item => this.selectedItems.has(item.id));
    if (selectedMedia.length === 0) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${selectedMedia.length} selected files?`,
      header: 'Delete Media Files',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const mediaIds = selectedMedia.map(item => item.id);
          const success = await this.mediaService.batchDeleteMedia(mediaIds);
          
          if (success) {
            // Remove from local array
            this.media = this.media.filter(item => !this.selectedItems.has(item.id));
            this.selectedItems.clear();
            this.selectAll = false;
            this.updateBatchMenuLabels();
            
            // Emit events for deleted items
            mediaIds.forEach(id => this.mediaDeleted.emit(id));
            
            this.notificationService.showToast('success', 'Success', `${selectedMedia.length} files deleted successfully`);
          } else {
            this.notificationService.showToast('error', 'Error', 'Failed to delete files');
          }
        } catch (error) {
          this.notificationService.showToast('error', 'Error', 'Failed to delete files');
        }
      }
    });
  }

  // Gallery methods
  openGalleria(index: number) {
    this.selectedMediaIndex = index;
    this.displayGalleria = true;
    
    // PERFORMANCE FIX: Only cache current item, let user navigation trigger others
    const currentItem = this.filteredMedia[index];
    if (currentItem && currentItem.url) {
      // Cache current item with delay to avoid blocking gallery open
      setTimeout(() => {
        this.loadPhotoWithCache(currentItem, false);
      }, 100);
    }
  }

  openVideoGalleria(index: number) {
    // Map video index to overall media index for gallery
    const videoItem = this.filteredVideos[index];
    const mediaIndex = this.filteredMedia.findIndex(item => item.id === videoItem.id);
    this.openGalleria(mediaIndex);
  }

  openPhotoGalleria(index: number) {
    // Map photo index to overall media index for gallery
    const photoItem = this.filteredPhotos[index];
    const mediaIndex = this.filteredMedia.findIndex(item => item.id === photoItem.id);
    this.openGalleria(mediaIndex);
  }

  // Filtering and sorting
  get filteredMedia(): MediaItem[] {
    let filtered = this.media;
    
    if (this.filterValue) {
      const filter = this.filterValue.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(filter) ||
        item.label?.toLowerCase().includes(filter)
      );
    }
    
    return filtered;
  }

  get filteredVideos(): MediaItem[] {
    return this.filteredMedia.filter(item => this.isVideo(item));
  }

  get filteredPhotos(): MediaItem[] {
    return this.filteredMedia.filter(item => this.isImage(item));
  }
  
  get visibleMedia(): MediaItem[] {
    // If controls are hidden, the parent component is managing what to show
    // So just return the filtered media without additional photo/video filtering
    if (!this.showControls) {
      return this.filteredMedia;
    }
    // If controls are shown, filter by the selected media type
    return this.selectedMediaType === 'photos' ? this.filteredPhotos : this.filteredVideos;
  }

  getSortOptions() {
    return [
      { label: '📅 Date', value: 'date', icon: 'pi pi-calendar' },
      { label: '📝 Name', value: 'name', icon: 'pi pi-file' },
      { label: '📏 Size', value: 'size', icon: 'pi pi-chart-bar' },
      { label: '🏷️ Label', value: 'label', icon: 'pi pi-tag' }
    ];
  }

  getSortOrderIcon(): string {
    if (this.sortOrder === 'asc') {
      switch (this.sortBy) {
        case 'date': return 'pi pi-sort-alpha-up';
        case 'name': return 'pi pi-sort-alpha-up';
        case 'size': return 'pi pi-sort-numeric-up';
        case 'label': return 'pi pi-sort-alpha-up';
        default: return 'pi pi-sort-up';
      }
    } else {
      switch (this.sortBy) {
        case 'date': return 'pi pi-sort-alpha-down';
        case 'name': return 'pi pi-sort-alpha-down';
        case 'size': return 'pi pi-sort-numeric-down';
        case 'label': return 'pi pi-sort-alpha-down';
        default: return 'pi pi-sort-down';
      }
    }
  }

  getSortOrderLabel(): string {
    const sortName = this.getSortOptions().find(opt => opt.value === this.sortBy)?.label.replace(/[📅📝📏🏷️]/g, '').trim() || 'Date';
    return this.sortOrder === 'asc' ? `${sortName} ↑` : `${sortName} ↓`;
  }

  getSortTooltip(): string {
    const currentSort = this.getSortOptions().find(opt => opt.value === this.sortBy)?.label.replace(/[📅📝📏🏷️]/g, '').trim() || 'Date';
    const currentOrder = this.sortOrder === 'asc' ? 'ascending' : 'descending';
    const nextOrder = this.sortOrder === 'asc' ? 'descending' : 'ascending';
    return `Currently sorted by ${currentSort} (${currentOrder}). Click to sort ${nextOrder}.`;
  }

  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortMedia();
  }

  sortMedia() {
    this.media.sort((a, b) => {
      let valueA: any, valueB: any;
      
      switch (this.sortBy) {
        case 'name':
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
        case 'date':
          valueA = new Date(a.createdAt).getTime();
          valueB = new Date(b.createdAt).getTime();
          break;
        case 'size':
          valueA = a.size;
          valueB = b.size;
          break;
        case 'label':
          valueA = (a.label || '').toLowerCase();
          valueB = (b.label || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      return this.sortOrder === 'asc' ? comparison : -comparison;
    });
  }

  // Photo caching methods
  getPhotoUrl(item: MediaItem | null, useThumbnail: boolean = true): string {
    if (!item) return '';
    
    // Return cached URL if available, otherwise original URL
    if (useThumbnail) {
      const url = item.cachedThumbnailUrl || item.thumbnailUrl || item.url;
      // Ensure we return a valid URL
      return url || '';
    } else {
      const url = item.cachedUrl || item.url;
      // Ensure we return a valid URL
      return url || '';
    }
  }

  loadPhotoWithCache(item: MediaItem, useThumbnail: boolean = true): void {
    // Performance toggle - skip caching if disabled
    if (!this.enablePhotoCaching) return;
    
    const url = useThumbnail ? (item.thumbnailUrl || item.url) : item.url;
    
    // Return early if no valid URL or already cached
    if (!url || !url.trim()) return;
    if (useThumbnail && item.cachedThumbnailUrl) return;
    if (!useThumbnail && item.cachedUrl) return;

    // Load photo through cache service
    this.photoCacheService.getPhotoBlob(url).subscribe({
      next: (blobUrl) => {
        if (blobUrl && blobUrl !== url) { // Only update if we got a different (cached) URL
          if (useThumbnail) {
            item.cachedThumbnailUrl = blobUrl;
          } else {
            item.cachedUrl = blobUrl;
          }
        }
      },
      error: (error) => {
        console.warn(`Failed to cache photo: ${url}`, error);
      }
    });
  }

  preloadVisibleThumbnails(): void {
    // PERFORMANCE FIX: Much less aggressive caching
    // Only cache the first few visible items to avoid blocking photo capture
    const visibleItems = this.filteredMedia.slice(0, this.maxCacheItems);
    
    // Load thumbnails with longer delays to avoid blocking UI
    visibleItems.forEach((item, index) => {
      if (item.url || item.thumbnailUrl) {
        // Stagger with longer delays (500ms between each)
        setTimeout(() => {
          this.loadPhotoWithCache(item, true);
        }, index * 500);
      }
    });

    // Skip full image preloading for now - it was too aggressive
    // Users can click to view full images when needed
  }

  private getVisibleItemsCount(): number {
    // Calculate approximate number of visible items based on grid size
    const columns = this.gridSettings.columns;
    const estimatedRows = 3; // Estimate 3 rows visible on average screen
    return columns * estimatedRows;
  }

  // Video/Media type detection
  isVideo(item: MediaItem | null): boolean {
    if (!item) return false;
    return item.type?.startsWith('video/') || false;
  }
  
  isImage(item: MediaItem | null): boolean {
    if (!item) return false;
    return item.type?.startsWith('image/') || !this.isVideo(item); // Default to image if no type
  }
  
  getMediaIcon(item: MediaItem): string {
    if (this.isVideo(item)) {
      return 'pi pi-play-circle';
    }
    return 'pi pi-image';
  }

  // Utility methods
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getRelativeTime(date: Date | undefined | null): string {
    if (!date) return 'Unknown';
    
    const now = new Date();
    const dateObj = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    
    return dateObj.toLocaleDateString();
  }

  getStatusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' {
    switch (status) {
      case 'ready': return 'success';
      case 'uploading': return 'info';
      case 'error': return 'danger';
      default: return 'info';
    }
  }

  getStatusLabel(status?: string): string {
    switch (status) {
      case 'ready': return 'Ready';
      case 'uploading': return 'Uploading';
      case 'error': return 'Error';
      default: return 'Processing';
    }
  }

  // Track by function for ngFor performance
  trackByMediaId(index: number, item: MediaItem): string {
    return item.id;
  }

  // Image error handler - show placeholder when S3 images fail to load
  onImageError(event: any) {
    const img = event.target;
    // Use a placeholder image or create a simple colored rectangle
    img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIFVuYXZhaWxhYmxlPC90ZXh0Pgo8L3N2Zz4=';
    img.style.border = '1px solid #ddd';
    img.style.borderRadius = '4px';
  }

  // Photo preview dialog properties
  isCircularNavigation: boolean = true;

  // Photo preview dialog methods
  getCurrentPhoto(): MediaItem | null {
    return this.filteredMedia[this.selectedMediaIndex] || null;
  }

  closePhotoPreview(): void {
    this.displayGalleria = false;
  }

  nextPhoto(): void {
    if (this.selectedMediaIndex < this.filteredMedia.length - 1) {
      this.selectedMediaIndex++;
    } else if (this.isCircularNavigation) {
      this.selectedMediaIndex = 0;
    }
    
    // PERFORMANCE FIX: Only cache current image on demand
    const currentItem = this.filteredMedia[this.selectedMediaIndex];
    if (currentItem && currentItem.url) {
      setTimeout(() => {
        this.loadPhotoWithCache(currentItem, false);
      }, 50); // Small delay to avoid blocking navigation
    }
  }

  previousPhoto(): void {
    if (this.selectedMediaIndex > 0) {
      this.selectedMediaIndex--;
    } else if (this.isCircularNavigation) {
      this.selectedMediaIndex = this.filteredMedia.length - 1;
    }
    
    // PERFORMANCE FIX: Only cache current image on demand
    const currentItem = this.filteredMedia[this.selectedMediaIndex];
    if (currentItem && currentItem.url) {
      setTimeout(() => {
        this.loadPhotoWithCache(currentItem, false);
      }, 50); // Small delay to avoid blocking navigation
    }
  }

  // Keyboard navigation support
  onKeydown(event: KeyboardEvent): void {
    if (!this.displayGalleria) return;
    
    switch (event.key) {
      case 'ArrowLeft':
        this.previousPhoto();
        event.preventDefault();
        break;
      case 'ArrowRight':
        this.nextPhoto();
        event.preventDefault();
        break;
      case 'Escape':
        this.closePhotoPreview();
        event.preventDefault();
        break;
    }
  }

  // Galleria responsive options (legacy - can be removed later)
  galleriaResponsiveOptions = [
    { breakpoint: '1024px', numVisible: 5 },
    { breakpoint: '768px', numVisible: 3 },
    { breakpoint: '560px', numVisible: 1 }
  ];
  
  // Periodic refresh methods to catch videos processed after session ends
  startPeriodicRefresh(): void {
    console.log('🔄 Starting periodic refresh for video processing...');
    
    // Start refreshing after 1 minute to give videos time to process
    setTimeout(() => {
      this.refreshInterval = setInterval(() => {
        if (this.refreshCount >= this.maxRefreshAttempts) {
          console.log('🔄 Stopping periodic refresh after maximum attempts');
          this.stopPeriodicRefresh();
          return;
        }
        
        this.refreshCount++;
        console.log(`🔄 Periodic refresh attempt ${this.refreshCount}/${this.maxRefreshAttempts}`);
        
        // Check if new media (especially videos) have been added
        this.refreshMediaIfNeeded();
      }, this.refreshIntervalMs);
    }, 60000); // Wait 1 minute before starting
  }
  
  stopPeriodicRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }
  
  private async refreshMediaIfNeeded(): Promise<void> {
    if (!this.sessionId) return;
    
    try {
      // Check composition status first
      await this.checkCompositionStatus();
      
      const currentCount = this.media.length;
      const response = await this.http.get<any>(`${this.apiUrl}/session/${this.sessionId}`).toPromise();
      
      if (response?.mediaFiles && response.mediaFiles.length > currentCount) {
        const newMediaCount = response.mediaFiles.length - currentCount;
        console.log(`✅ Found ${newMediaCount} new media files, refreshing gallery...`);
        
        this.media = response.mediaFiles.map((item: any) => ({
          ...item,
          selected: false
        }));
        this.sortMedia();
        
        // Check if any of the new media are videos
        const hasNewVideos = response.mediaFiles.some((item: any) => 
          item.type?.startsWith('video/') && 
          !this.media.find(existing => existing.id === item.id)
        );
        
        if (hasNewVideos) {
          console.log('🎥 New videos detected in media gallery!');
        }
        
        // Don't stop refreshing if we still have active compositions
        if (!this.hasActiveCompositions) {
          this.stopPeriodicRefresh();
        }
      }
    } catch (error) {
      console.warn('⚠️ Error during periodic media refresh:', error);
    }
  }
  
  private async checkCompositionStatus(): Promise<void> {
    if (!this.sessionId) return;
    
    try {
      this.compositionStatus = await this.mediaService.getSessionCompositions(this.sessionId);
      
      if (this.compositionStatus) {
        const { summary } = this.compositionStatus;
        this.hasActiveCompositions = (summary.pending + summary.processing) > 0;
        
        console.log('🎬 Composition Status:', {
          pending: summary.pending,
          processing: summary.processing,
          completed: summary.completed,
          error: summary.error
        });
        
        // If we have completed compositions, refresh media to show them
        if (summary.completed > 0) {
          console.log('✅ Found completed video compositions!');
        }
      }
    } catch (error) {
      console.warn('⚠️ Error checking composition status:', error);
    }
  }
}
